import {
    Accordion,
    AccordionSummary,
    Badge,
    Box,
    Grid,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import { useNavigate } from 'react-router-dom';
import UserRole from '../../../../shared/AuthRoles';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import GradeIcon from '@mui/icons-material/Grade';

export default function AccesControlList() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const navigate = useNavigate();
    const loggedInUser = UserRole();

    const handleChange = (url: string) => () => {
        navigate(url);
    };

    const allSettings = [
        {
            group: 'Visibility',
            items: [
                {
                    name: 'Pricing Access Control',
                    description:
                        'Hide or show experience costs for different employee role groups. Since many experiences are billed directly to the company, you can choose to hide pricing details from employees to avoid discouraging participation, while still making costs visible to admins or relevant stakeholders.',
                    url: '/accessControl/pricing',
                    icon: <MoneyOffIcon />,
                    roles: ['guest_admin'],
                },
                {
                    name: 'VIP Access Control',
                    description:
                        'Give your top employees the exclusive treatment they deserve. With VIP Access Control, you can mark specific experiences as VIP-only, ensuring that only employees on your curated VIP list are able to book them. Perfect for offering premium perks, rewarding loyalty, or keeping certain experiences private.',
                    url: '/accessControl/VIP',
                    icon: <GradeIcon />,
                    roles: ['guest_admin'],
                },
            ],
        },
        {
            group: 'Booking Caps',
            items: [
                {
                    name: 'Booking Caps',
                    description:
                        'Booking caps give you more control over who can book experiences and how often. By creating a booking cap, you can limit how many times a user or your entire organisation can book an experience per week, month, or year.',
                    url: '/bookingCaps',
                    icon: <CalendarMonthIcon />,
                    roles: ['guest_admin'],
                },
            ],
        },
    ];

    const filteredGroups = allSettings
        .map((group) => ({
            ...group,
            items: group.items.filter((item) =>
                item.roles.includes(loggedInUser?.role || 'default'),
            ),
        }))
        .filter((group) => group.items.length > 0);

    return (
        <Grid sx={{ width: '100%', marginTop: '30px' }}>
            {filteredGroups.map((group) => (
                <Box key={group.group} sx={{ mb: 2 }}>
                    <Typography
                        component={'h2'}
                        sx={{
                            color: 'text.primary',
                            fontWeight: 'bold',
                        }}
                    >
                        {group.group}
                    </Typography>
                    {group.items.map(({ name, description, url, icon }) => {
                        return (
                            <Accordion
                                key={name}
                                expanded={false}
                                onChange={handleChange(url)}
                                elevation={0}
                                disableGutters
                                sx={{
                                    border: 'none',
                                    margin: '5px 0',
                                    minHeight: '40px',
                                    '&:hover': {
                                        backgroundColor:
                                            'rgba(92, 181, 179, 0.1)',
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        <ArrowRightIcon
                                            sx={{ fontSize: '20px' }}
                                        />
                                    }
                                    aria-controls={`${name}-content`}
                                    id={`${name}-header`}
                                    sx={{
                                        minHeight: '40px',
                                        padding: '5px 10px',
                                        '& .MuiAccordionSummary-content': {
                                            margin: 0,
                                        },
                                    }}
                                >
                                    <ListItem sx={{ padding: 0 }}>
                                        <ListItemAvatar>
                                            <Avatar
                                                sx={{
                                                    width: 35,
                                                    height: 35,
                                                    bgcolor: 'lightGrey.main',
                                                    color: 'lightGrey.contrastText',
                                                }}
                                            >
                                                {icon}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    component={'h3'}
                                                    variant='subtitle1'
                                                >
                                                    {name}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography
                                                    variant='body3'
                                                    color='text.primary'
                                                    sx={{
                                                        mt: 0.25,
                                                        whiteSpace: 'normal',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient:
                                                            'vertical',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {description}
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                </AccordionSummary>
                            </Accordion>
                        );
                    })}
                </Box>
            ))}
        </Grid>
    );
}
